import React from "react";
// eslint-disable-next-line
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Chip,
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableTable from "../../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../../components/ReusableComponent/ReusableDeleteDialog";
import ExpandingResumeSubscriptionDetails from "./ExpandingResumeSubscriptionDetails";
import AddEditResumeSubscription from "./AddEditResumeSubscription";
import FiltersModal, { VacancyFiltersType } from "./FiltersModal";
import { ResumeSubscription } from "../../../../../../types/orders-managment/subscriptions";

// ==============================|| resume subscriptionsS LIST ||============================== //

const ResumeSubscriptions = () => {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<ResumeSubscription | null>(null);
  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const handleFiltersModalClose = () => {
    setOpenFilters(false);
  };

  const handleRowExpand = (rowId: string | number) => {
    setExpandedRowId(expandedRowId === rowId ? null : rowId);
  };

  const columns = useMemo<ColumnDef<ResumeSubscription>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Company Name" />,
        accessorKey: "company_name",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.company_name}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Total Resumes" />,
        accessorKey: "amount_resumes",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.amount_resumes}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Used Resumes" />,
        accessorKey: "used_resumes",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.used_resumes}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Start Date" />,
        accessorKey: "start_date",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.start_date}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="End Date" />,
        accessorKey: "end_date",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.end_date}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Chip
            label={row.original.status ? "Active" : "Inactive"}
            color={row.original.status ? "success" : "error"}
            variant="filled"
            size="small"
          />
        ),
      },
      {
        header: () => <FormattedMessage id="Created At" />,
        accessorKey: "created_at",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.created_at}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Updated At" />,
        accessorKey: "updated_at",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.updated_at}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const isExpanded = expandedRowId === row.original.id;
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "Collapse" : "View Details"}>
                <IconButton
                  color="secondary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleRowExpand(row.original.id);
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResumeSubscription[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchedtext, setSearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/resume_subscriptions/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "resume subscriptions deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to delete resume subscriptions",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get("id");
      const company_id = urlParams.get("company_id");
      const type = urlParams.get("type");
      const from = urlParams.get("from");
      const to = urlParams.get("to");
      const status = urlParams.get("status");

      const response = await USERAXIOS.get(
        `/resume_subscriptions?page=${currentPage}${
          searchQuery ? `&id=${searchQuery}` : ""
        }` +
          (id ? `&id=${id}` : "") +
          (company_id ? `&company_id=${company_id}` : "") +
          (type ? `&type=${type}` : "") +
          (from ? `&from=${from}` : "") +
          (to ? `&to=${to}` : "") +
          (status ? `&status=${status}` : "")
      );
      const { data, meta } = response.data;
      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching resume subscriptionss",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  const handleApplyFilters = (filters: VacancyFiltersType) => {
    setLoading(true);
    const { id, company_id, duration, from, to, status } = filters;
    const urlParams = new URLSearchParams(window.location.search);

    // Clear existing filter params
    urlParams.delete("id");
    urlParams.delete("company_id");
    urlParams.delete("duration");
    urlParams.delete("from");
    urlParams.delete("to");
    urlParams.delete("status");

    // Set new filter params
    if (id) urlParams.set("id", id);
    if (company_id) urlParams.set("company_id", company_id);
    if (duration) urlParams.set("duration", duration);
    if (from) urlParams.set("from", from);
    if (to) urlParams.set("to", to);
    if (status) urlParams.set("status", status);

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);

    setCurrenttPage(1);
    fetchData();
  };

  const handleClearFilters = () => {
    setLoading(true);
    window.history.pushState({}, "", `${window.location.pathname}`);
    setCurrenttPage(1);
    fetchData();
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("id") ||
      urlParams.get("company_id") ||
      urlParams.get("duration") ||
      urlParams.get("from") ||
      urlParams.get("to") ||
      urlParams.get("status")
    );
  };

  const enhancedData = useMemo(() => {
    return result.map((item) => ({
      ...item,
      getCanExpand: () => true,
      getIsExpanded: () => expandedRowId === item.id,
      getToggleExpandedHandler: () => () => handleRowExpand(item.id),
    }));
  }, [result, expandedRowId]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              {hasActiveFilters() && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClearFilters}
                  sx={{ padding: ".3rem 2rem" }}
                >
                  <FormattedMessage id="Clear Filters" />
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<FilterOutlined />}
                onClick={() => setOpenFilters(true)}
                sx={{
                  padding: ".3rem 2rem",
                  gap: ".5rem",
                }}
              >
                <FormattedMessage id="Filters" />
              </Button>
            </Box>
          </Box>

          <ReusableTable
            data={enhancedData}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearsh}
            searchedtext={searchedtext}
            setSearchedtext={setSearchedtext}
            modalToggler={() => {
              setCustomerModal(true);
              setSelectedCustomer(null);
            }}
            renderDetailPanel={({ row }: any) => (
              <ExpandingResumeSubscriptionDetails data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          handleClose={handleFiltersModalClose}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
      )}

      <ReusableDeleteDialog
        title={selected?.company_id}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      {customerModal && (
        <AddEditResumeSubscription
          open={customerModal}
          setResult={setResult}
          loading={formLoading}
          setLoading={setFormLoading}
          fetchData={fetchData}
          id={selected}
          customer={selectedCustomer}
          handleClose={() => setCustomerModal(false)}
        />
      )}
    </>
  );
};

export default ResumeSubscriptions;
